import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Typography, Button, Alert, CircularProgress } from "@mui/material";
import CalendarApi, { CalendarStatus } from "../api/CalendarApi";

/** Psychologist opt-in two-way calendar sync (Google / Microsoft Outlook). */
export default function CalendarSync() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<CalendarStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const load = () => CalendarApi.status().then((s) => { setStatus(s); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const param = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("calendar") : null;
  const connect = async (p: "google" | "microsoft") => {
    setErr("");
    try { const { url } = await CalendarApi.connect(p); window.location.href = url; }
    catch { setErr(t("calendar.connectError")); }
  };
  const disconnect = async () => { await CalendarApi.disconnect(); load(); };

  if (loading) return <CircularProgress size={24} sx={{ color: "primary.main" }} />;
  const conn = status?.connection;
  const cfg = status?.configured || { google: false, microsoft: false };
  const providerLabel = conn?.provider === "google" ? "Google Calendar" : "Microsoft Outlook";

  return (
    <Stack spacing={2}>
      <Typography sx={{ color: "text.secondary", m: 0 }}>{t("calendar.intro")}</Typography>
      {param === "connected" && <Alert severity="success">{t("calendar.connected")}</Alert>}
      {param === "error" && <Alert severity="error">{t("calendar.connectError")}</Alert>}
      {err && <Alert severity="error">{err}</Alert>}
      {conn ? (
        <>
          <Alert severity="success">{t("calendar.connectedWith", { provider: providerLabel })}</Alert>
          <Button variant="outlined" color="error" onClick={disconnect} sx={{ alignSelf: "flex-start" }}>{t("calendar.disconnect")}</Button>
        </>
      ) : !cfg.google && !cfg.microsoft ? (
        <Alert severity="info">{t("calendar.notConfigured")}</Alert>
      ) : (
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1.5 }}>
          {cfg.google && <Button variant="contained" onClick={() => connect("google")}>{t("calendar.connectGoogle")}</Button>}
          {cfg.microsoft && <Button variant="contained" onClick={() => connect("microsoft")}>{t("calendar.connectMicrosoft")}</Button>}
        </Stack>
      )}
    </Stack>
  );
}
